import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  Filter,
  Plus,
  Search,
  ChevronDown,
  Eye,
  MessageSquare
} from 'lucide-react';
import Button from '../../../components/common/Button';
import Input from '../../../components/common/Input';
import Card from '../../../components/ui/Card';
import DataTable from '../../../components/ui/DataTable';
import Badge from '../../../components/ui/Badge';
import { useQuotations } from '../../../hooks/useQuotations';

const QuotationsPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const { 
    quotations, 
    loading, 
    stats,
    updateQuotation,
    deleteQuotation,
    downloadQuotation 
  } = useQuotations();

  const filteredQuotations = quotations.filter((quote) => {
    if (activeTab === 'all') return true;
    return quote.status === activeTab;
  });

  const tabs = [
    { id: 'all', label: 'All', count: quotations.length },
    { id: 'pending', label: 'Pending', count: stats.pending },
    { id: 'accepted', label: 'Accepted', count: stats.accepted },
    { id: 'rejected', label: 'Rejected', count: stats.rejected },
    { id: 'expired', label: 'Expired', count: stats.expired }
  ];

  const columns = [
    {
      id: 'quoteId',
      header: 'Quote ID',
      accessor: 'id',
      render: (item) => (
        <Link to={`/buyer/quotations/${item.id}`} className="text-blue-600 hover:underline font-medium">
          #{item.id.slice(-8)}
        </Link>
      )
    },
    {
      id: 'product',
      header: 'Product',
      accessor: 'product.name',
      render: (item) => (
        <div className="flex items-center">
          <img
            src={item.product.imageUrl || '/api/placeholder/40/40'}
            alt={item.product.name}
            className="h-10 w-10 rounded object-cover mr-3"
          />
          <div>
            <div className="font-medium text-gray-900">{item.product.name}</div>
            <div className="text-xs text-gray-500">SKU: {item.product.sku}</div>
          </div>
        </div>
      )
    },
    {
      id: 'supplier',
      header: 'Supplier',
      accessor: 'supplier.name'
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessor: 'quantity'
    },
    {
      id: 'price',
      header: 'Price',
      accessor: 'price',
      render: (item) => `$${item.price.toLocaleString()}`
    },
    {
      id: 'total',
      header: 'Total',
      render: (item) => `$${(item.price * item.quantity).toLocaleString()}`
    },
    {
      id: 'status',
      header: 'Status',
      accessor: 'status',
      render: (item) => {
        const statusConfig = {
          pending: { color: 'yellow', icon: Clock },
          accepted: { color: 'green', icon: CheckCircle },
          rejected: { color: 'red', icon: XCircle },
          expired: { color: 'gray', icon: AlertCircle }
        };
        const config = statusConfig[item.status] || { color: 'gray', icon: FileText };
        const Icon = config.icon;
        
        return (
          <Badge
            variant={config.color}
            className="inline-flex items-center"
          >
            <Icon className="h-3 w-3 mr-1" />
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </Badge>
        );
      }
    },
    {
      id: 'expires',
      header: 'Expires',
      accessor: 'expiresAt',
      render: (item) => {
        const expires = new Date(item.expiresAt);
        const now = new Date();
        const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        
        return (
          <div className={`text-sm ${daysLeft < 3 ? 'text-red-600' : 'text-gray-600'}`}>
            {daysLeft > 0 ? `${daysLeft} days` : 'Expired'}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      render: (item) => (
        <div className="flex space-x-2">
          <Button
            size="small"
            variant="outline"
            onClick={() => window.location.href = `/buyer/quotations/${item.id}`}
          >
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
          {item.status === 'pending' && (
            <>
              <Button
                size="small"
                variant="success"
                onClick={() => updateQuotation(item.id, { status: 'accepted' })}
              >
                Accept
              </Button>
              <Button
                size="small"
                variant="danger"
                onClick={() => updateQuotation(item.id, { status: 'rejected' })}
              >
                Reject
              </Button>
            </>
          )}
          <Button
            size="small"
            variant="ghost"
            onClick={() => downloadQuotation(item.id)}
          >
            <Download className="h-3 w-3" />
          </Button>
        </div>
      )
    }
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search logic
    console.log('Search:', searchQuery);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
          <p className="text-gray-600">Manage your price quotes from suppliers</p>
        </div>
        <Link to="/buyer/quotations/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Quote Request
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Quotes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Accepted</p>
              <p className="text-2xl font-bold text-gray-900">{stats.accepted}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
            </div>
            <div className="p-2 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Tabs */}
        <div className="flex space-x-1 border-b">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex items-center space-x-3">
          <form onSubmit={handleSearch} className="relative">
            <Input
              placeholder="Search quotes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="h-4 w-4 text-gray-400" />}
              className="w-64"
              size="small"
            />
          </form>
          
          <Button
            variant="outline"
            size="small"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filter
            <ChevronDown className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option>Last 30 days</option>
                <option>Last 90 days</option>
                <option>This year</option>
                <option>Custom range</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Value
              </label>
              <Input
                type="number"
                placeholder="$0"
                size="small"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Supplier Type
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2">
                <option>All Suppliers</option>
                <option>Verified Only</option>
                <option>Premium Suppliers</option>
              </select>
            </div>
          </div>
        </Card>
      )}

      {/* Quotations Table */}
      <Card>
        <DataTable
          columns={columns}
          data={filteredQuotations}
          loading={loading}
          pagination={{
            page: 1,
            limit: 10,
            total: filteredQuotations.length,
            pages: Math.ceil(filteredQuotations.length / 10)
          }}
          onPageChange={(page) => console.log('Page:', page)}
          selection={true}
          exportable={true}
          onExport={() => console.log('Export quotes')}
        />
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg mr-4">
              <MessageSquare className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Need Help?</h3>
              <p className="text-sm text-gray-600">Get support with your quotes</p>
            </div>
          </div>
          <Button variant="outline" fullWidth>
            Contact Support
          </Button>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-green-100 rounded-lg mr-4">
              <Download className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Export Data</h3>
              <p className="text-sm text-gray-600">Download all your quotations</p>
            </div>
          </div>
          <Button variant="outline" fullWidth>
            Export All
          </Button>
        </Card>
        
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-purple-100 rounded-lg mr-4">
              <FileText className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Templates</h3>
              <p className="text-sm text-gray-600">Use quote request templates</p>
            </div>
          </div>
          <Button variant="outline" fullWidth>
            View Templates
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default QuotationsPage;